import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authApi } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { useMe } from "@/hooks/useMe";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";

const SIDEBAR_COLLAPSED_KEY = "kgu-sidebar-collapsed";

/**
 * Декодує JWT base64url payload (вирішує проблему з `-`/`_` що atob не приймає).
 * Повертає null при будь-якій помилці.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;
    // base64url → base64
    const b64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const clearAuth = useAuthStore((s) => s.clear);
  const accessToken = useAuthStore((s) => s.accessToken);

  const { data: me, isLoading, isError } = useMe();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1",
  );
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // active_tenant_id з access-токена (поки /auth/me його не повертає).
  // Перерахунок при зміні токена.
  const activeTenantIdFromJwt = useMemo<string | null>(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken);
    const tid = payload?.["tenant_id"];
    return typeof tid === "string" ? tid : null;
  }, [accessToken]);

  const switchTenant = useMutation({
    mutationFn: authApi.switchTenant,
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  // Side-effects на isError робимо в useEffect, щоб не порушувати React-правило
  // «не змінюй state під час рендера». Раніше clearAuth/navigate викликалися
  // прямо в render-функції — це давало StrictMode warnings.
  useEffect(() => {
    if (isError) {
      clearAuth();
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }, [isError, clearAuth, queryClient, navigate]);

  if (isError) {
    return null;
  }

  if (isLoading || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar
        role={me.role}
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          me={me}
          activeTenantId={activeTenantIdFromJwt}
          onTenantChange={(id) => switchTenant.mutate(id)}
        />
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
